import { motion, AnimatePresence } from "framer-motion";
import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";
import { useDrag } from "../hooks/common/useDrag";
import { twMerge } from "tailwind-merge";
import { useSyncProp } from "../hooks/common/useSyncProp";

interface BottomSheetProps {
  minHeight?: number;
  defaultVisible?: boolean;
  onClose?: () => unknown;
  children?: ReactNode;
  hasFullBtn?: boolean;
}
export const BottomSheet = ({
  minHeight,
  defaultVisible,
  children,
  hasFullBtn,
  onClose,
}: BottomSheetProps) => {
  const [visible, setVisible] = useSyncProp(defaultVisible);
  const btnRef = useRef<HTMLButtonElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isFullRef = useRef<"full" | undefined>();
  const initialHeight = useRef(0);
  const {
    pos: { y },
    state,
  } = useDrag(btnRef, { trigger: !!visible });
  const handleClose = useCallback(() => {
    setVisible(false);
    onClose?.();
  }, [onClose, setVisible]);
  const height = useMemo(() => {
    if (initialHeight.current === 0 && wrapRef.current) {
      initialHeight.current = wrapRef.current.scrollHeight;
    }
    if (state === "end") {
      if (y < -100) {
        isFullRef.current = "full";
        return window.innerHeight - (btnRef.current?.offsetHeight ?? 0);
      } else if (
        y > 100 &&
        (wrapRef.current?.scrollHeight ?? 0) > initialHeight.current
      ) {
        isFullRef.current = undefined;
        return initialHeight.current;
      } else if (
        y > 100 &&
        (wrapRef.current?.scrollHeight ?? 0) < initialHeight.current
      ) {
        isFullRef.current = undefined;
        handleClose?.();
      }
    }
    if (state === "move") {
      const start =
        isFullRef.current === "full"
          ? window.innerHeight - (btnRef.current?.offsetHeight ?? 0)
          : initialHeight.current;
      return start - y;
    }
    // console.log(, "!@#!@#!@#");
    return Math.max(wrapRef.current?.scrollHeight ?? 0, minHeight ?? 0);
  }, [y, state, handleClose, minHeight]);
  useEffect(() => {
    if (!wrapRef.current || !visible) return;
    wrapRef.current.style.height = "auto";
    wrapRef.current.style.height = `${wrapRef.current.scrollHeight}px`;
  }, [visible]);
  return createPortal(
    <AnimatePresence>
      {visible && (
        <>
          <motion.div
            className={twMerge(
              "flex flex-col z-20 pt-0 rounded-t-3xl bg-white fixed bottom-14 w-full drop-shadow-[0_25px_25px_rgb(0_0_0_/_0.95)]"
            )}
            initial={{
              transform: "translateY(100%)",
            }}
            animate={{
              transform: "translateY(0)",
            }}
            exit={{
              transform: "translateY(100%)",
            }}
          >
            {hasFullBtn && (
              <Button className="flex p-4 m-auto w-fit" ref={btnRef}>
                <i className="rounded-full h-1.5 w-32 bg-zinc-300" />
              </Button>
            )}
            <div
              ref={wrapRef}
              className={twMerge(
                "flex-auto p-4",
                state !== "move" && "transition-all"
              )}
              style={{
                height,
              }}
            >
              {children}
            </div>
          </motion.div>

          <motion.i
            initial={{ opacity: 0 }}
            animate={{
              opacity: 0.2,
            }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black"
            onClick={handleClose}
          />
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
